/*
 * TaxPack - the SARS tax-year pack, in Rands. Pulls /budget/tax-pack/: every
 * expense you flagged tax-deductible, grouped into SARS buckets, plus a travel
 * logbook built from your tracked trips. Pick the tax year (1 Mar..end Feb),
 * print it or export CSV, and hand it to your accountant. This ORGANISES what
 * you logged - it is not tax advice; you and your accountant decide what
 * actually qualifies.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, tokens } from '../api.js';
import { fmtR } from '../money.js';
import AppNav from '../components/AppNav.jsx';
import { useAuth } from '../App.jsx';

const BUCKET_LABELS = {
  travel: 'Travel', home_office: 'Home office', donations: 'Donations',
  medical: 'Medical', retirement: 'Retirement', business: 'Business',
};

const BUCKET_ICONS = {
  travel: '🚗', home_office: '🏠', donations: '🎁',
  medical: '💊', retirement: '🏦', business: '💼',
};

const CERT_KINDS = [
  { kind: 'medical_aid', label: 'Medical-Aid tax certificate' },
  { kind: 'it3b', label: 'IT3(b) \u2014 bank / investment income' },
  { kind: 'ra', label: 'Retirement Annuity certificate' },
];

// Optional supporting docs — attach only when they apply to this taxpayer.
const OPTIONAL_CERT_KINDS = [
  { kind: 'irp5', label: 'IRP5 / IT3(a)' },
  { kind: 'travel_allow', label: 'Travel allowance certificate' },
  { kind: 'home_office', label: 'Home-office supporting docs' },
  { kind: 's18a', label: 'S18A donation certificate' },
];

export default function TaxPack() {
  const { me } = useAuth();
  const now = new Date();
  const defaultEnd = now.getMonth() <= 1 ? now.getFullYear() : now.getFullYear() + 1;
  const [year, setYear] = useState(defaultEnd);
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nonce, setNonce] = useState(0);
  const [busyCert, setBusyCert] = useState('');
  const [openBucket, setOpenBucket] = useState(null);
  const [tab, setTab] = useState('deductible');
  const [fixTrip, setFixTrip] = useState(null);   // held-back trip being named
  const [fixVal, setFixVal] = useState('');

  useEffect(() => {
    let live = true;
    setLoading(true); setError('');
    (async () => {
      try {
        const d = await api(`/budget/tax-pack/?year=${year}`);
        if (live) setPack(d);
      } catch (e) {
        if (live) setError(e?.data?.detail || e?.message || 'Could not load your tax pack.');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [year, nonce]);

  const years = [];
  for (let y = defaultEnd; y >= defaultEnd - 5; y--) years.push(y);

  // Pack readiness (Pass 7 Tax): three checks straight off the /tax-pack payload,
  // amber where something is short - no invented numbers.
  const _rItems = pack ? (pack.buckets || []).flatMap((b) => b.items || []) : [];
  const nItems = _rItems.length;
  const nBuckets = pack ? (pack.buckets || []).length : 0;
  const noReceipt = _rItems.filter((it) => !it.receipt).length;
  const withReceipt = nItems - noReceipt;
  const rTrips = pack ? (pack.travel_logbook || []) : [];
  const heldTrips = rTrips.filter((t) => !t.client).length;
  const missingCerts = pack ? (pack.missing_certificates || []) : [];
  const certsIn = 3 - missingCerts.length;
  const checks = [
    { key: 'items', label: 'Every deductible item is flagged', ok: nItems > 0,
      detail: nItems ? `${nItems} item${nItems === 1 ? '' : 's'} across ${nBuckets} bucket${nBuckets === 1 ? '' : 's'} · ${fmtR(pack ? pack.total_deductible : 0)}` : 'Nothing flagged for this year yet',
      tag: nItems ? null : 'nothing yet' },
    { key: 'receipts', label: 'Every flagged item has a receipt', ok: nItems > 0 && noReceipt === 0,
      detail: nItems ? `${withReceipt} of ${nItems} have a receipt` : 'no flagged items to check',
      tag: noReceipt > 0 ? `${noReceipt} with no receipt` : null },
    { key: 'trips', label: 'Every business trip names a client', ok: heldTrips === 0,
      detail: rTrips.length ? `${rTrips.length - heldTrips} of ${rTrips.length} name a client · ${pack ? pack.travel_total_km : 0} km` : 'no business trips logged',
      tag: heldTrips > 0 ? `${heldTrips} held back — no client` : null },
    { key: 'certs', label: 'All three certificates are attached', ok: missingCerts.length === 0,
      detail: `${certsIn} of 3 required certificates attached`,
      tag: missingCerts.length ? `${missingCerts.length} missing` : null },
  ];
  const _applicable = checks.filter((c) => !(c.key === 'receipts' && nItems === 0) && !(c.key === 'trips' && rTrips.length === 0));
  const readyPct = _applicable.length ? Math.round((100 * _applicable.filter((c) => c.ok).length) / _applicable.length) : 0;
  const readyLine = [`${certsIn} of 3 certificates`, heldTrips ? `${heldTrips} trip${heldTrips === 1 ? '' : 's'} unnamed` : null, noReceipt ? `${noReceipt} without a receipt` : null].filter(Boolean).join(' · ');
  const _short = [];
  if (nItems === 0) _short.push('flag your deductible expenses');
  if (noReceipt > 0) _short.push(`add receipts to ${noReceipt} flagged item${noReceipt === 1 ? '' : 's'}`);
  if (heldTrips > 0) _short.push(`name the client on ${heldTrips} trip${heldTrips === 1 ? '' : 's'}`);
  if (missingCerts.length) _short.push(`attach ${missingCerts.length} certificate${missingCerts.length === 1 ? '' : 's'}`);
  const readyNote = _short.length ? `To finish the pack: ${_short.join('; ')}.` : 'This pack is complete — every check passes. Print it or export the CSV for your accountant.';
  const maxBucket = Math.max(1, ...(pack ? (pack.buckets || []).map((b) => Number(b.total) || 0) : [0]));
  const CERT_FOR_BUCKET = { medical: 'medical_aid', retirement: 'ra' };
  const unflag = async (id) => {
    try { await api(`/budget/items/${id}/`, { method: 'PATCH', body: { tax_deductible: false } }); setNonce((n) => n + 1); }
    catch (e) { setError(e?.data?.detail || e?.message || 'Could not unflag that item.'); }
  };
  const TAX_TABS = [
    { key: 'deductible', label: 'Deductible', count: nBuckets ? `${nBuckets}` : '' },
    { key: 'travel', label: 'Travel logbook', count: rTrips.length ? `${rTrips.length}` : '' },
    { key: 'certs', label: 'Certificates', count: `${certsIn}/3` },
  ];

  const exportCsv = () => {
    if (!pack) return;
    const rows = [['Section', 'Date', 'Item', 'Category', 'Amount (ZAR)']];
    (pack.buckets || []).forEach((b) => {
      (b.items || []).forEach((it) => {
        rows.push([BUCKET_LABELS[b.bucket] || b.bucket, it.date || '', it.title || '',
                   it.category || '', it.amount || '0']);
      });
    });
    (pack.travel_logbook || []).forEach((t) => {
      rows.push(['Travel logbook', t.date || '',
                 `${t.label || ''}${t.client ? ' \u2014 ' + t.client : ''}`,
                 `${t.distance_km || 0} km`, t.value_at_rate || '0']);
    });
    saveCsv(rows, `vault-tax-pack-${year}.csv`);
  };

  // The travel logbook on its own — the practitioner files it separately from
  // the deductibles (Vault Tax Pack.dc.html). Held-back (no-client) trips carry
  // their status so nothing blank is mistaken for a filed trip.
  const exportLogbookCsv = () => {
    if (!pack) return;
    const rows = [['Date', 'Trip', 'Client', 'Kilometres', 'Value at SARS rate (ZAR)', 'Cost (ZAR)', 'Status']];
    (pack.travel_logbook || []).forEach((t) => {
      rows.push([t.date || '', t.label || '', t.client || '',
                 `${t.distance_km || 0}`, t.value_at_rate || '0', t.cost || '0',
                 t.held_back ? 'held back — no client' : 'in the pack']);
    });
    saveCsv(rows, `vault-travel-logbook-${year}.csv`);
  };

  const saveCsv = (rows, filename) => {
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  // Receipts zip — filed one folder per SARS bucket, images converted to PDF —
  // is streamed behind the JWT (never a public URL), so fetch it as an
  // authenticated blob and save it.
  const downloadReceiptsZip = async () => {
    try {
      const r = await fetch(`/api/budget/tax-pack/receipts/?year=${year}`,
                            { headers: { Authorization: `Bearer ${tokens.access}` } });
      if (!r.ok) throw new Error('download failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `vault-tax-receipts-${year}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || 'Could not build the receipts zip.');
    }
  };

  // PRIMARY hand-over: pull the CSV, the logbook CSV and the receipts zip, then
  // open a pre-written email to the practitioner to attach them to. (A tracked
  // read receipt needs a stored practitioner address — deferred, needs a
  // migration — so this composes the mail rather than sending server-side.)
  const [sendTo, setSendTo] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState('');
  useEffect(() => { if (me?.email && !sendTo) setSendTo(me.email); }, [me]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Real hand-over: the server builds the whole pack and EMAILS it with every
  // document attached — the totals + travel-logbook CSV, the receipts (PDF), and
  // the certificates. Send to the practitioner, or to yourself to test.
  const sendToPractitioner = async () => {
    const to = (sendTo || me?.email || '').trim();
    if (!to) { setError('Enter a recipient email.'); return; }
    setSending(true); setError(''); setSent('');
    try {
      const r = await api('/budget/tax-pack/send/', { method: 'POST', body: { to, year } });
      const a = r?.attached || {};
      setSent(`Emailed to ${r.to}: totals CSV${a.receipts_zip ? ' + receipts' : ''}`
              + `${a.certificates ? ` + ${a.certificates} certificate(s)` : ''}.`);
    } catch (e) {
      setError(e?.data?.detail || e?.message || 'Could not send the pack.');
    } finally { setSending(false); }
  };

  const uploadCert = async (kind, file) => {
    if (!file) return;
    setBusyCert(kind); setError('');
    try {
      const fd = new FormData();
      fd.append('kind', kind); fd.append('tax_year', String(year)); fd.append('file', file);
      await api('/budget/tax-certificates/', { method: 'POST', form: fd });
      setNonce((n) => n + 1);
    } catch (e) {
      setError(e?.data?.detail || e?.message || 'Upload failed.');
    } finally { setBusyCert(''); }
  };
  const deleteCert = async (id) => {
    try { await api(`/budget/tax-certificates/${id}/`, { method: 'DELETE' }); setNonce((n) => n + 1); }
    catch { /* ignore */ }
  };
  // Name the client on a held-back trip right from its logbook row — once named
  // it stops being held back and joins the total (Pass 8 D, fixable inline).
  const saveTripClient = async (id) => {
    const name = fixVal.trim();
    if (!name) { setFixTrip(null); return; }
    try {
      await api(`/budget/trips/${id}/`, { method: 'PATCH', body: { client_name: name } });
      setFixTrip(null); setFixVal(''); setNonce((n) => n + 1);
    } catch (e) { setError(e?.data?.detail || e?.message || 'Could not save the client.'); }
  };

  return (
    <div className="min-h-screen bg-ink text-slate-100">
      <AppNav active="tax" />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6">
        <h1 className="text-xl font-bold mb-4">Tax pack</h1>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="inline-flex items-center gap-1 border border-edge bg-surface rounded-full p-1" data-guide="tax-year">
            <button className="w-7 h-7 rounded-full hover:bg-surface2 text-slate-400" onClick={() => setYear(year - 1)} aria-label="Previous tax year">‹</button>
            <span className="text-sm font-semibold px-2 min-w-[74px] text-center">{`${year - 1} / ${String(year).slice(2)}`}</span>
            <button className="w-7 h-7 rounded-full hover:bg-surface2 text-slate-400 disabled:opacity-30" onClick={() => setYear(year + 1)} disabled={year >= defaultEnd} aria-label="Next tax year">›</button>
          </div>
          <div className="flex-1" />
          <span className="text-xs text-slate-500">SA tax year · 1 Mar – 28 Feb</span>
        </div>

        <p className="text-xs text-slate-500 leading-relaxed mb-5">
          This gathers every expense you flagged tax-deductible for the chosen SARS
          tax year and sorts it into buckets, all in Rands, with your travel logbook
          from tracked trips. It only organises what you have logged - you and your
          accountant decide what actually qualifies.
        </p>

        {loading && <p className="text-slate-500 text-sm">Loading&hellip;</p>}
        {error && <p className="text-red-400 text-sm">{error}</p>}

        {pack && !loading && (
          <>
            {/* Pack readiness - three checks, amber where short (Pass 7 Tax). */}
            <div data-guide="tax-readiness" className="rounded-2xl border border-edge bg-surface p-5 mb-6">
              <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Pack readiness</div>
              <div className="flex items-baseline gap-3 mt-1">
                <div className={`text-4xl font-extrabold ${readyPct === 100 ? 'text-emerald-400' : 'text-amber-400'}`}>{readyPct}%</div>
                <div className="text-sm text-slate-400 min-w-0">{readyLine}</div>
              </div>
              <div className="h-1.5 rounded-full bg-surface2 overflow-hidden mt-3 mb-3">
                <div className={`h-full rounded-full ${readyPct === 100 ? 'bg-emerald-400' : 'bg-amber-400'}`} style={{ width: `${readyPct}%` }} />
              </div>
              <p className="text-xs text-slate-400 leading-relaxed mb-4">{readyNote}</p>
              <div className="rounded-xl border border-edge overflow-hidden">
                {checks.map((c) => (
                  <div key={c.key} className="flex items-center gap-3 px-3 py-2.5 border-b border-edge/60 last:border-0 text-sm">
                    <span className={`w-5 h-5 shrink-0 grid place-items-center rounded-full text-[11px] ${c.ok ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>{c.ok ? '✓' : '!'}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block">{c.label}</span>
                      <span className="block text-xs text-slate-500">{c.detail}</span>
                    </span>
                    {c.tag && <span className="text-[11px] text-amber-400 shrink-0 whitespace-nowrap">{c.tag}</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Four stat tiles (Pass 7 Tax redesign) - overview above the tabs. */}
            <div data-guide="tax-totals" className="grid gap-3 grid-cols-2 lg:grid-cols-4 mb-5">
              <div className="rounded-2xl border border-edge bg-surface p-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Deductible total</div>
                <div className="text-2xl font-extrabold text-emerald-400 mt-1">{fmtR(pack.total_deductible)}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{nBuckets ? `across ${nBuckets} bucket${nBuckets === 1 ? '' : 's'}` : 'nothing flagged yet'}</div>
              </div>
              <div className="rounded-2xl border border-edge bg-surface p-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Business travel</div>
                <div className="text-2xl font-extrabold mt-1">{pack.travel_total_km} km</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{rTrips.length ? `${rTrips.length - heldTrips} of ${rTrips.length} with a client` : 'no trips logged'}</div>
              </div>
              <div className="rounded-2xl border border-edge bg-surface p-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Certificates</div>
                <div className={`text-2xl font-extrabold mt-1 ${missingCerts.length ? 'text-amber-400' : 'text-emerald-400'}`}>{certsIn} of 3</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{missingCerts.length ? `${missingCerts.length} still to attach` : 'all three attached'}</div>
              </div>
              <div className="rounded-2xl border border-edge bg-surface p-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Receipts</div>
                <div className="text-2xl font-extrabold mt-1">{withReceipt}</div>
                <div className={`text-[11px] mt-0.5 ${noReceipt ? 'text-amber-400' : 'text-slate-500'}`}>{noReceipt ? `${noReceipt} flagged with no receipt` : (nItems ? 'all have a receipt' : 'nothing flagged yet')}</div>
              </div>
            </div>

            {/* Tabs (Pass 7 Tax redesign). */}
            <div className="flex flex-wrap gap-2 mb-5">
              {TAX_TABS.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-3.5 py-1.5 rounded-full text-sm border transition ${tab === t.key ? 'border-vblue bg-vblue/10 text-vblue' : 'border-edge text-slate-400 hover:border-vblue/50'}`}>
                  {t.label}
                  {t.count ? <span className={`ml-2 text-[11px] px-1.5 py-0.5 rounded-full ${tab === t.key ? 'bg-vblue/20' : 'bg-surface2 text-slate-500'}`}>{t.count}</span> : null}
                </button>
              ))}
            </div>

            {/* ---- DEDUCTIBLE TAB ---- */}
            {tab === 'deductible' && (
              <div>
                {(pack.buckets || []).length === 0 ? (
                  <p className="text-slate-500 text-sm">
                    Nothing flagged for this tax year yet. Open any expense, go to its Tax
                    tab, tick &ldquo;Tax-deductible&rdquo; and choose a bucket - it lands here.
                  </p>
                ) : (
                  <>
                    <h2 className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500 mb-2">Deductible by SARS bucket</h2>
                    {(pack.buckets || []).map((b) => {
                      const bItems = b.items || [];
                      const rc = bItems.filter((x) => x.receipt).length;
                      const certNeeded = CERT_FOR_BUCKET[b.bucket] && missingCerts.includes(CERT_FOR_BUCKET[b.bucket]);
                      const pct = Math.round((100 * (Number(b.total) || 0)) / maxBucket);
                      return (
                        <div key={b.bucket} className="mb-3">
                          <div className="card !p-0 overflow-hidden">
                            <button className="w-full flex items-center gap-3 px-4 py-3 text-left" onClick={() => setOpenBucket((o) => o === b.bucket ? null : b.bucket)}>
                              <span className="w-9 h-9 shrink-0 rounded-xl bg-surface2 border border-edge grid place-items-center text-lg">{BUCKET_ICONS[b.bucket] || '🧾'}</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold">{BUCKET_LABELS[b.bucket] || b.bucket}</span>
                                  {certNeeded && <span className="text-[10px] uppercase tracking-wide text-amber-400 border border-amber-400/40 rounded px-1.5 py-0.5">certificate needed</span>}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {bItems.length} expense{bItems.length === 1 ? '' : 's'}{rc ? ` · ${rc} with a receipt` : ' · no receipts yet'}
                                </div>
                                <div className="h-1 rounded-full bg-surface2 overflow-hidden mt-1.5 max-w-[240px]">
                                  <div className="h-full rounded-full bg-vblue/70" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                              <span className="font-bold text-emerald-400 shrink-0">{fmtR(b.total)}</span>
                              <span className="text-slate-500 text-sm shrink-0">{openBucket === b.bucket ? '▾' : '▸'}</span>
                            </button>
                            {openBucket === b.bucket && (
                              <div className="border-t border-edge divide-y divide-edge/60">
                                {bItems.map((it) => (
                                  <div key={it.id} className="flex items-center gap-3 px-4 py-2 text-sm">
                                    <span className="text-slate-500 w-24 shrink-0">{it.date || '-'}</span>
                                    <span className="flex-1 min-w-0 truncate">{it.title}</span>
                                    {it.receipt
                                      ? <a className="text-[11px] text-emerald-400 hover:underline shrink-0" href={it.receipt} target="_blank" rel="noreferrer">🧾 receipt</a>
                                      : <span className="text-[11px] text-amber-400 shrink-0">no receipt</span>}
                                    <span className="w-24 text-right font-medium shrink-0">{fmtR(it.amount)}</span>
                                    <button className="text-[11px] text-slate-500 hover:text-red-400 shrink-0" onClick={() => unflag(it.id)}>Unflag</button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}

            {/* ---- TRAVEL LOGBOOK TAB ---- */}
            {tab === 'travel' && (
              <div>
                <div className="flex items-start gap-3 rounded-xl border border-vblue/30 bg-vblue/5 px-4 py-3 mb-4 text-sm text-slate-300">
                  <span>🚗</span>
                  <p className="leading-relaxed">The logbook only helps if the travel is genuine business travel. Each trip needs a date, the kilometres and a client or purpose — trips with no client name are held back from the total rather than sent blank.</p>
                </div>
                {(pack.travel_logbook || []).length === 0 ? (
                  <p className="text-slate-500 text-sm">No business trips logged for this tax year yet.</p>
                ) : (
                  <>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <h2 className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500">Trips this tax year</h2>
                      <span className="text-sm font-bold">{pack.travel_total_km} km · {fmtR(pack.travel_value_at_rate)}</span>
                    </div>
                    <div className="rounded-xl border border-edge overflow-hidden">
                      {pack.travel_logbook.map((t, i) => (
                        <div key={t.id || i} className="px-3 py-2 border-b border-edge/60 last:border-0 text-sm">
                          <div className="flex items-center gap-3">
                            <span className="text-slate-500 w-24 shrink-0">{t.date || '-'}</span>
                            <span className="flex-1 min-w-0 truncate">{t.label}{t.client ? <span className="text-slate-500"> · {t.client}</span> : ''}</span>
                            {t.held_back
                              ? (fixTrip === t.id
                                  ? null
                                  : <button className="text-[11px] text-amber-400 shrink-0 underline"
                                            onClick={() => { setFixTrip(t.id); setFixVal(''); }}>
                                      held back — name client
                                    </button>)
                              : <span className="text-[11px] text-emerald-400/80 shrink-0">in the pack</span>}
                            <span className="w-20 text-right text-slate-400 shrink-0">{t.distance_km} km</span>
                            <span className="w-24 text-right font-medium shrink-0">{t.held_back ? '\u2014' : fmtR(t.value_at_rate)}</span>
                          </div>
                          {fixTrip === t.id && (
                            <div className="flex items-center gap-2 mt-2">
                              <input className="input flex-1 !py-1.5" autoFocus value={fixVal}
                                     placeholder="Client or purpose of this business trip"
                                     onChange={(e) => setFixVal(e.target.value)}
                                     onKeyDown={(e) => e.key === 'Enter' && saveTripClient(t.id)} />
                              <button className="border border-vblue/50 bg-vblue/10 text-vblue rounded-xl px-3 py-1.5 text-xs shrink-0"
                                      onClick={() => saveTripClient(t.id)}>Save</button>
                              <button className="text-xs text-slate-500 shrink-0" onClick={() => setFixTrip(null)}>Cancel</button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-2">
                      Travel is valued at the SARS prescribed rate of {fmtR(pack.travel_rate_per_km)}/km for the {pack.travel_rate_year} tax year &mdash; {fmtR(pack.travel_value_at_rate)} across {pack.travel_total_km} client-named business km. This deemed figure is kept separate from any actual travel costs; your practitioner chooses which method to use. Name the client on a held-back trip to include it.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* ---- CERTIFICATES TAB ---- */}
            {tab === 'certs' && (
              <div data-guide="tax-certificates">
                <p className="text-xs text-slate-500 mb-3">
                  Medical Aid, IT3(b) and RA are <span className="text-slate-300">required</span> — the pack is not complete without all three, though you can send early and mark them as following. Upload a PDF or photo.
                </p>
                <div className="rounded-xl border border-edge overflow-hidden">
                  {CERT_KINDS.map((c) => {
                    const got = (pack.certificates || []).find((x) => x.kind === c.kind);
                    return (
                      <div key={c.kind} className="flex items-center gap-3 px-3 py-2.5 border-b border-edge/60 last:border-0 text-sm">
                        <span className="flex-1 min-w-0">
                          {c.label}
                          <span className={`ml-2 text-[10px] uppercase tracking-wide ${got ? 'text-emerald-400/80' : 'text-amber-400'}`}>{got ? 'attached' : 'required — missing'}</span>
                        </span>
                        {got ? (
                          <>
                            <a className="text-vblue text-xs hover:underline" href={got.file} target="_blank" rel="noreferrer">view</a>
                            <button className="text-slate-500 hover:text-red-400 text-xs" onClick={() => deleteCert(got.id)}>remove</button>
                          </>
                        ) : (
                          <label className="text-xs text-vblue hover:underline cursor-pointer">
                            {busyCert === c.kind ? 'Uploading…' : 'Upload'}
                            <input type="file" accept="image/*,application/pdf" className="hidden"
                                   onChange={(e) => uploadCert(c.kind, e.target.files?.[0])} />
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-4 mb-2">
                  Optional — attach only if they apply: IRP5, travel allowance, home office, S18A donations.
                </p>
                <div className="rounded-xl border border-edge overflow-hidden">
                  {OPTIONAL_CERT_KINDS.map((c) => {
                    const got = (pack.certificates || []).find((x) => x.kind === c.kind);
                    return (
                      <div key={c.kind} className="flex items-center gap-3 px-3 py-2.5 border-b border-edge/60 last:border-0 text-sm">
                        <span className="flex-1 min-w-0">
                          {c.label}
                          <span className={`ml-2 text-[10px] uppercase tracking-wide ${got ? 'text-emerald-400/80' : 'text-slate-500'}`}>{got ? 'attached' : 'optional'}</span>
                        </span>
                        {got ? (
                          <>
                            <a className="text-vblue text-xs hover:underline" href={got.file} target="_blank" rel="noreferrer">view</a>
                            <button className="text-slate-500 hover:text-red-400 text-xs" onClick={() => deleteCert(got.id)}>remove</button>
                          </>
                        ) : (
                          <label className="text-xs text-vblue hover:underline cursor-pointer">
                            {busyCert === c.kind ? 'Uploading…' : 'Upload'}
                            <input type="file" accept="image/*,application/pdf" className="hidden"
                                   onChange={(e) => uploadCert(c.kind, e.target.files?.[0])} />
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
                {pack.practitioner_note && (
                  <p className="text-[11px] text-slate-500 italic mt-3">{pack.practitioner_note}</p>
                )}
              </div>
            )}

            {/* Hand-over — "Send to your practitioner" is the PRIMARY action; the
                CSV, receipts zip and logbook CSV sit beside it (Pass 8 D). */}
            <h2 className="text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500 mt-6 mb-2">Hand it over</h2>
            <div className="card" data-guide="tax-export">
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <input type="email" value={sendTo} onChange={(e) => setSendTo(e.target.value)}
                       placeholder="practitioner@email.co.za"
                       className="flex-1 bg-transparent border border-edge rounded-xl px-4 py-2.5 text-sm text-slate-100 outline-none focus:border-vblue" />
                <button className="btn-primary !px-5 !py-2.5 text-sm whitespace-nowrap"
                        onClick={sendToPractitioner} disabled={!pack || sending}>
                  {sending ? 'Sending…' : '📨 Email the pack'}
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Emails everything as attachments — the totals + travel-logbook CSV, the
                receipts (PDF), and the certificates. Send it to your practitioner, or to
                yourself to test.
              </p>
              {sent && <p className="text-[11px] text-emerald-400 mt-1">{sent}</p>}
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-edge">
                <button className="border border-edge rounded-xl px-4 py-2 text-sm hover:border-vblue transition"
                        onClick={exportCsv} disabled={!pack}>📥 CSV</button>
                <button className="border border-edge rounded-xl px-4 py-2 text-sm hover:border-vblue transition"
                        onClick={downloadReceiptsZip} disabled={!pack}>🧾 Receipts (zip)</button>
                <button className="border border-edge rounded-xl px-4 py-2 text-sm hover:border-vblue transition"
                        onClick={exportLogbookCsv} disabled={!pack || !rTrips.length}>🚗 Logbook CSV</button>
                <button className="border border-edge rounded-xl px-4 py-2 text-sm hover:border-vblue transition"
                        onClick={() => window.print()}>🖨 Print / Save as PDF</button>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">Vault organises &amp; totals — you and your accountant decide what qualifies.</p>
            <footer className="text-center text-slate-600 text-[11px] mt-8 pt-4 border-t border-edge">Created by <b className="text-slate-400">Thevertex</b></footer>
          </>
        )}
      </div>
    </div>
  );
}
